import React, { useCallback, useMemo, useRef } from 'react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import {
	MAX_LABEL_LENGTH,
	STATE_COLOURS,
	TRANSITION_STROKE_WIDTH,
} from '../../constants/diagram.tsx';
import { EDGE_TYPE } from '../../constants/index.tsx';
import {
	type Point,
	type TransitionIcons,
	ValidationFlag,
	toPoint,
	type ConnectionPorts,
} from '../../types/diagram.tsx';
import type { ItemReference } from '../../types/index.tsx';
import { areConnectionsEqual, truncateLabel } from '../../utils/index.tsx';
import { DATA_ATTRIBUTE_IGNORE_AUTOFIT } from '../../utils/use-auto-layout/utils.tsx';
import { useLayoutWidth } from '../../utils/use-layout-width/index.tsx';
import { useStrokeColor } from '../../utils/use-stroke/index.tsx';

import { IconSet } from './icon-set/index.tsx';

export interface Props {
	id: string;
	label?: string;
	icons?: TransitionIcons[];
	flag?: ValidationFlag;
	isHovered?: boolean;
	isHighlighted?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	isOverlapping?: boolean;
	isOverlapHovered?: boolean;
	collapseTransitionLabels?: boolean;
	position?: Point | undefined;
	maxLabelLength?: number;
	// TODO: change connection to required on hix-7062_shared_transition_show_label_highlighted cleanup
	connection?: ConnectionPorts;
	hoveredItem?: ItemReference;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
}

const TEXT_Y_OFFSET = 16;
const ICON_LABEL_GAP = 2;
const ICON_PADDING = 2;
const ICON_ONLY_LABEL_PADDING = 16;
const LABEL_PADDING = 20;
const TEXT_X_IF_NO_ICONS = 20;
const LABEL_HEIGHT = 24;
const TEXT_OFFSET = 6;
const ERROR_RING_DASH_ARRAY = '3,2';
const FOCUS_RING_WIDTH = 4;
const SVG_VISIBLE = {};
const SVG_HIDDEN_AND_HOVERABLE = { opacity: 0 };
const SVG_HIDDEN_NOT_HOVERABLE = { visibility: 'hidden' };
const SVG_HIDDEN = { opacity: 0 };

const TransitionLabel = ({
	id,
	label = '',
	flag,
	icons = [],
	isHovered = false,
	isHighlighted = false,
	isImmutable = false,
	isSelected = false,
	isTargeted = false,
	position,
	isOverlapping = false,
	isOverlapHovered = false,
	maxLabelLength = MAX_LABEL_LENGTH,
	onSelect,
	onHover,
	collapseTransitionLabels = false,
	connection,
	hoveredItem,
}: Props) => {
	const textRef = useRef<SVGTextElement>(null);
	const iconGroupRef = useRef<SVGGElement>(null);
	const textWidth = useLayoutWidth(textRef, [label, icons, position]);
	const iconsWidth = useLayoutWidth(iconGroupRef, [label, icons, position]);
	const labelWidth =
		label === ''
			? textWidth + iconsWidth + ICON_ONLY_LABEL_PADDING
			: textWidth + iconsWidth + LABEL_PADDING;
	const textTransformX = iconsWidth ? TEXT_X_IF_NO_ICONS : (labelWidth - textWidth) / 2;
	const textX = iconsWidth && iconsWidth - TEXT_OFFSET;

	const stroke = useStrokeColor({
		flag,
		isHighlighted,
		isHovered,
		isSelected,
	});

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [isImmutable, onSelect, id]);

	const onMouseEnter = useCallback(() => {
		if (fg('hix-7062_shared_transition_show_label_highlighted')) {
			onHover?.({ id, type: EDGE_TYPE, connection });
		} else {
			onHover?.({ id, type: EDGE_TYPE });
		}
	}, [id, onHover, connection]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	const visibilityProps = useMemo(() => {
		if (fg('hix-7062_shared_transition_show_label_highlighted')) {
			if (!collapseTransitionLabels || isSelected || isTargeted) return SVG_VISIBLE;

			const isConnectionHovered =
				hoveredItem?.type === EDGE_TYPE && areConnectionsEqual(connection, hoveredItem?.connection);

			return isConnectionHovered ? SVG_VISIBLE : SVG_HIDDEN;
		}
		const canCollapseLabel = collapseTransitionLabels && !isHovered && !isSelected && !isTargeted;
		if (!canCollapseLabel || isOverlapHovered) {
			return SVG_VISIBLE;
		}

		return isOverlapping ? SVG_HIDDEN_AND_HOVERABLE : SVG_HIDDEN_NOT_HOVERABLE;
	}, [
		isOverlapping,
		collapseTransitionLabels,
		isOverlapHovered,
		isHovered,
		isSelected,
		isTargeted,
		hoveredItem,
		connection,
	]);

	if (icons.length === 0 && label === '') {
		return null;
	}

	const truncatedLabel = truncateLabel(label, maxLabelLength);

	if (!position) {
		return null;
	}

	const labelX = position.x - labelWidth / 2;
	const labelY = position.y - LABEL_HEIGHT / 2;

	const hasLabelText = label !== '';
	const hasError = flag === ValidationFlag.Error;
	const hasWarning = flag === ValidationFlag.Warn;
	const hasValidationFlag = hasError || hasWarning;

	return (
		<g
			data-testid="accessible-workflow-diagram.common.ui.diagram-transition-label.transition-label"
			{...visibilityProps}
			onClick={onGroupClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			// See HIX-3948 - There is a bug in Safari (last tested on v17.1) where getBBox for this element
			// is always { x: 0, y: 0 } even after the element has been rendered, unless there is label text.
			// To mitigate this we're using this attribute to filter this element out of calculations.
			{...(!label && DATA_ATTRIBUTE_IGNORE_AUTOFIT)}
		>
			{/* Target ring */}
			{hasLabelText && isTargeted && (
				<rect
					x={labelX}
					y={labelY}
					width={labelWidth}
					height={LABEL_HEIGHT}
					rx={LABEL_HEIGHT / 2}
					ry={LABEL_HEIGHT / 2}
					stroke={STATE_COLOURS.focused}
					strokeWidth={FOCUS_RING_WIDTH * 2}
				/>
			)}
			{/* Label background */}
			<rect
				x={labelX}
				y={labelY}
				width={labelWidth}
				height={LABEL_HEIGHT}
				rx={LABEL_HEIGHT / 2}
				ry={LABEL_HEIGHT / 2}
				fill={token('color.background.input')}
				stroke={stroke}
				strokeWidth={TRANSITION_STROKE_WIDTH}
				strokeDasharray={!isSelected && hasValidationFlag ? ERROR_RING_DASH_ARRAY : undefined}
			/>
			{/* Icons */}
			<g ref={iconGroupRef}>
				<IconSet
					icons={icons}
					anchor={toPoint(labelX + ICON_LABEL_GAP + ICON_PADDING, labelY + ICON_PADDING)}
				/>
			</g>
			{/* Transition label text */}
			<text
				ref={textRef}
				x={textX + labelX + textTransformX}
				y={labelY + TEXT_Y_OFFSET}
				textAnchor="start"
				fontSize="12px"
				fill={token('color.text')}
			>
				{truncatedLabel}
			</text>
		</g>
	);
};

export default TransitionLabel;
